import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Pencil, Plus, Truck } from 'lucide-react'
import { api } from '../lib/api'
import { Card, CardContent, CardHeader } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Modal } from '../components/ui/Modal'
import { Badge } from '../components/ui/Badge'
import { usePermissions } from '../contexts/permissions'
import { apiError, fmtMoney } from '../lib/industria'
import type { SupplierSummary } from '../lib/compras'

type Form = {
  id: string | null; name: string; cnpj: string; email: string
  phone: string; leadTimeDays: string; notes: string; active: boolean
}

const empty: Form = {
  id: null, name: '', cnpj: '', email: '', phone: '', leadTimeDays: '', notes: '', active: true,
}

export function IndustriaFornecedoresPage() {
  const queryClient = useQueryClient()
  const { can } = usePermissions()
  const canEdit = can('industria-fornecedores', 'edit')

  const [open, setOpen] = useState(false)
  const [form, setForm] = useState<Form>(empty)
  const [error, setError] = useState<string | null>(null)

  const { data: suppliers = [], isLoading } = useQuery<SupplierSummary[]>({
    queryKey: ['compras', 'suppliers', 'summary'],
    queryFn: async () => (await api.get('/suppliers/summary')).data,
  })

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        name: form.name.trim(),
        cnpj: form.cnpj.trim() || null,
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        leadTimeDays: form.leadTimeDays ? Number(form.leadTimeDays) : null,
        notes: form.notes.trim() || null,
        active: form.active,
      }
      return form.id
        ? (await api.patch(`/suppliers/${form.id}`, payload)).data
        : (await api.post('/suppliers', payload)).data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['compras', 'suppliers'] })
      queryClient.invalidateQueries({ queryKey: ['industria', 'suppliers'] })
      setOpen(false)
    },
    onError: (e) => setError(apiError(e, 'Não foi possível salvar o fornecedor')),
  })

  const totalOpen = suppliers.reduce((a, s) => a + s.openAmount, 0)

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Fornecedores</h2>
          <p className="text-sm text-gray-500">
            O prazo de entrega daqui é o que transforma a necessidade líquida em data de compra
          </p>
        </div>
        {canEdit && (
          <Button onClick={() => { setForm(empty); setError(null); setOpen(true) }}>
            <Plus className="h-4 w-4" /> Novo fornecedor
          </Button>
        )}
      </div>

      {error && <p className="rounded-lg bg-red-50 px-4 py-2 text-sm text-red-700">{error}</p>}

      <Card>
        <CardHeader>
          <span className="text-sm font-semibold text-gray-900">{suppliers.length} fornecedor(es)</span>
          {totalOpen > 0 && (
            <span className="text-sm text-gray-600">{fmtMoney(totalOpen)} em aberto</span>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="px-5 py-10 text-center text-sm text-gray-400">Carregando...</p>
          ) : suppliers.length === 0 ? (
            <div className="px-5 py-12 text-center">
              <Truck className="mx-auto mb-3 h-8 w-8 text-gray-300" />
              <p className="text-sm text-gray-400">
                Nenhum fornecedor — sem prazo de entrega, a lista de compras não vira data
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-4 py-2.5 text-left font-medium">Fornecedor</th>
                    <th className="px-4 py-2.5 text-left font-medium">Contato</th>
                    <th className="px-4 py-2.5 text-right font-medium">Prazo</th>
                    <th className="px-4 py-2.5 text-right font-medium">Materiais</th>
                    <th className="px-4 py-2.5 text-right font-medium">Pedidos abertos</th>
                    <th className="px-4 py-2.5 text-right font-medium">A pagar</th>
                    <th className="px-4 py-2.5"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {suppliers.map(s => (
                    <tr key={s.id} className="hover:bg-gray-50/60">
                      <td className="px-4 py-2.5">
                        <span className="font-medium text-gray-900">{s.name}</span>
                        <span className="block font-mono text-xs text-gray-400">
                          {s.cnpj ?? '—'}{!s.active ? ' · inativo' : ''}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-gray-600">
                        {s.email ?? '—'}
                        {s.phone && <span className="block text-xs text-gray-400">{s.phone}</span>}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-700">
                        {s.leadTimeDays != null ? `${s.leadTimeDays} dias` : <span className="text-amber-600">—</span>}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-600">{s.materialCount}</td>
                      <td className="px-4 py-2.5 text-right">
                        {s.openPurchases > 0
                          ? <Badge variant="info">{s.openPurchases}</Badge>
                          : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-900">
                        {s.openAmount > 0 ? fmtMoney(s.openAmount) : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        {canEdit && (
                          <button
                            className="text-gray-300 hover:text-blue-600"
                            onClick={() => {
                              setForm({
                                id: s.id,
                                name: s.name,
                                cnpj: s.cnpj ?? '',
                                email: s.email ?? '',
                                phone: s.phone ?? '',
                                leadTimeDays: s.leadTimeDays != null ? String(s.leadTimeDays) : '',
                                notes: '',
                                active: s.active,
                              })
                              setError(null)
                              setOpen(true)
                            }}
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Modal
        title={form.id ? 'Editar fornecedor' : 'Novo fornecedor'}
        open={open}
        onClose={() => setOpen(false)}
      >
        <form
          className="space-y-4"
          onSubmit={e => { e.preventDefault(); setError(null); saveMutation.mutate() }}
        >
          <Input label="Nome" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
          <div className="grid grid-cols-2 gap-4">
            <Input label="CNPJ" value={form.cnpj} onChange={e => setForm({ ...form, cnpj: e.target.value })} />
            <Input
              label="Prazo de entrega (dias)"
              type="number"
              min={0}
              max={365}
              value={form.leadTimeDays}
              onChange={e => setForm({ ...form, leadTimeDays: e.target.value })}
            />
            <Input label="E-mail" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
            <Input label="Telefone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
          </div>
          <Input label="Observações" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300"
              checked={form.active}
              onChange={e => setForm({ ...form, active: e.target.checked })}
            />
            Ativo
          </label>

          {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button type="submit" loading={saveMutation.isPending}>Salvar</Button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
